import type { AppRouter } from "@immi/api"
import { QueryCache, QueryClient } from "@tanstack/react-query"
import {
  createTRPCClient,
  httpBatchLink,
  httpBatchStreamLink,
  splitLink,
} from "@trpc/client"
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query"
import { toast } from "sonner"

function makeQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.suppressErrorToast) return
        toast.error(error.message, {
          action: {
            label: "Tentar novamente",
            onClick: () => {
              query.invalidate()
            },
          },
        })
      },
    }),
  })
}

// Client components também renderizam no SERVIDOR: um QueryClient no escopo do
// módulo seria compartilhado entre requests (e entre contas). Singleton só no
// browser; no servidor, uma instância nova por request.
let browserQueryClient: QueryClient | undefined

export function getQueryClient(): QueryClient {
  if (typeof window === "undefined") return makeQueryClient()
  browserQueryClient ??= makeQueryClient()
  return browserQueryClient
}

/**
 * Compatibilidade: quem já importava `queryClient` continua funcionando, mas
 * agora resolve o client certo a cada acesso em vez de fixar o do módulo.
 * Em código novo prefira `useQueryClient()` (ou `getQueryClient()`).
 */
export const queryClient: QueryClient = new Proxy({} as QueryClient, {
  get(_target, prop) {
    const client = getQueryClient()
    const value = Reflect.get(client, prop, client)
    return typeof value === "function" ? value.bind(client) : value
  },
})

// Rotas que devolvem gerador precisam do link com stream. Ele fica restrito a
// elas: o stream manda os headers antes do corpo, e o resto do app continua no
// link em lote de sempre.
const STREAMING_PATHS = new Set(["postlab.review.create", "lessonTutor.ask"])

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    splitLink({
      condition: (op) => STREAMING_PATHS.has(op.path),
      true: httpBatchStreamLink({ url: "/api/trpc" }),
      false: httpBatchLink({ url: "/api/trpc" }),
    }),
  ],
})

export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient: getQueryClient,
})
